import { useRef, useState, type FormEvent } from "react";
import { createPortal } from "react-dom";
import { useFokusfalle } from "./useFokusfalle.ts";
import { todayIso } from "../../lib/format.ts";
import { fehlertext } from "../../lib/fehlertext.ts";
import type { FxDeal } from "../../lib/fx.ts";

export interface FxSicherungAnlegenEingabe {
  [feld: string]: unknown;
  accountId: string;
  /** Optional: die Sicherungsnummer "S-<n>" vergibt der Server. */
  no?: string;
  date: string;
  usd: number;
  bank: string;
  rate: number;
  docFileId: null;
}

export interface SicherungAnlegenKonto {
  id: string;
  name: string;
}

export interface SicherungAnlegenDialogProps {
  /** Nur aktive, buchbare Konten (`overview.accounts`). Ist genau eins dabei, ist es vorbelegt. */
  konten: readonly SicherungAnlegenKonto[];
  /** Vorauswahl, falls die Uebersicht gerade auf ein Konto gefiltert ist. */
  vorgewaehltesKontoId?: string | null;
  onSicherungAnlegen: (eingabe: FxSicherungAnlegenEingabe) => Promise<FxDeal>;
  onSchliessen: () => void;
}

export function SicherungAnlegenDialog({ konten, vorgewaehltesKontoId = null, onSicherungAnlegen, onSchliessen }: SicherungAnlegenDialogProps) {
  const [kontoId, setKontoId] = useState(() => (
    konten.length === 1 ? konten[0].id : konten.find((konto) => konto.id === vorgewaehltesKontoId)?.id ?? ""
  ));
  const [date, setDate] = useState(todayIso());
  const [usd, setUsd] = useState("");
  const [rate, setRate] = useState("");
  const [fehler, setFehler] = useState("");
  const [speichert, setSpeichert] = useState(false);
  const speichertRef = useRef(false);
  const dialogRef = useRef<HTMLElement>(null);
  const datumRef = useRef<HTMLInputElement>(null);

  useFokusfalle({ dialogRef, beschaeftigtRef: speichertRef, onSchliessen, ersterFokus: () => datumRef.current });

  async function speichern(ereignis: FormEvent<HTMLFormElement>): Promise<void> {
    ereignis.preventDefault();
    if (speichertRef.current) return;
    const betrag = Number(usd);
    const kurs = Number(rate);
    const konto = konten.find((eintrag) => eintrag.id === kontoId);
    if (!konto || !date || !Number.isFinite(betrag) || betrag <= 0 || !Number.isFinite(kurs) || kurs <= 0) {
      setFehler("Bitte alle Pflichtfelder gültig ausfüllen.");
      return;
    }
    setFehler("");
    speichertRef.current = true;
    setSpeichert(true);
    try {
      await onSicherungAnlegen({ accountId: konto.id, date, usd: betrag, bank: konto.name, rate: kurs, docFileId: null });
      onSchliessen();
    } catch (ursache) {
      // Inline bleibt inline (Spec R2): die Zeile im offenen Dialog gab es schon. Neu ist der Text - der Satz
      // zum Scheitern ist der Kontext, die Ursache (Netz, 502, Servertext ...) kommt aus `fehlertext()`;
      // ergibt `fehlertext` `null` (Abbruch), bleibt die Zeile leer.
      setFehler(fehlertext(ursache, "Die Sicherung konnte nicht angelegt werden.") ?? "");
    } finally {
      speichertRef.current = false;
      setSpeichert(false);
    }
  }

  return createPortal(
    <div className="board-editor-overlay open" role="presentation" onClick={(ereignis) => {
      if (!speichertRef.current && ereignis.target === ereignis.currentTarget) onSchliessen();
    }}>
      <section ref={dialogRef} className="board-editor-dialog fx-sicherung-anlegen-dialog" role="dialog" aria-modal="true" aria-labelledby="sicherungAnlegenTitel" tabIndex={-1}>
        <form className="password-form" onSubmit={(ereignis) => void speichern(ereignis)}>
          <div className="board-editor-head"><div className="board-editor-head-copy"><h2 id="sicherungAnlegenTitel">Neue Sicherung</h2><p>Offene USD-Sicherung für die spätere Übernahme erfassen.</p></div></div>
          <div className="board-editor-body fx-sicherung-anlegen-fields">
            {konten.length === 0 && <p className="fx-overview-error" role="alert">Es gibt kein aktives Konto. Eine Sicherung kann erst angelegt werden, wenn ein Konto aktiv ist.</p>}
            <label>Datum<input ref={datumRef} aria-label="Datum" type="date" value={date} disabled={speichert} onChange={(ereignis) => setDate(ereignis.target.value)} required /></label>
            <label>USD-Betrag<input aria-label="USD-Betrag" type="number" min="0.01" step="0.01" value={usd} disabled={speichert} onChange={(ereignis) => setUsd(ereignis.target.value)} required /></label>
            <label>Bank<select aria-label="Bank" value={kontoId} disabled={speichert || konten.length === 0} onChange={(ereignis) => setKontoId(ereignis.target.value)} required>{konten.length !== 1 && <option value="">Bank wählen …</option>}{konten.map((konto) => <option key={konto.id} value={konto.id}>{konto.name}</option>)}</select></label>
            <label>EUR/USD-Kurs<input aria-label="EUR/USD-Kurs" type="number" min="0.000001" step="0.000001" value={rate} disabled={speichert} onChange={(ereignis) => setRate(ereignis.target.value)} required /></label>
            {fehler && <p className="fx-overview-error" role="alert">{fehler}</p>}
          </div>
          <div className="board-editor-foot"><button className="btn-sec" type="button" disabled={speichert} onClick={onSchliessen}>Abbrechen</button><button className="btn-primary" type="submit" disabled={speichert || !konten.some((konto) => konto.id === kontoId)}>{speichert ? "Wird gespeichert …" : "Sicherung speichern"}</button></div>
        </form>
      </section>
    </div>,
    document.body,
  );
}
